import axios, { CancelToken } from 'axios';
import { makeRequest } from 'js/services/Network';

/**
 * Service wrapper for issuing Shipper requests
 *
 * @class Shipper
 */
export default class Shipper {
  cancelRatesRequest() {
    if (this.requestCancelToken) {
      this.requestCancelToken.cancel();
    }
  }

  /**
   * Grab shipping options for a given itemAlias, destination address, and size.
   * The currently logged in user must be the owner of the item or be the destination for this operation
   * to work.
   *
   * @static
   * @param string itemAlias
   * @param object destination address
   * @param string size
   * @memberof Shipper
   */
  getShippingOptions(itemAlias, destination, size = 'any') {
    const shippingClients = window.app.shippingClients;

    const clients = Object.keys(shippingClients).reduce((clientList, clientName) => {
      if (size === 'any' || shippingClients[clientName].sizes.includes(size)) {
        clientList.push(clientName);
      }
      return clientList;
    }, []);

    this.requestCancelToken = CancelToken.source();

    return new Promise((resolve, reject) => {
      const responses = [];
      const rejections = [];

      /**
       * Check overall status of all requests and trigger parent reject/resolve if appropriate
       */
      function rejectOrResolve() {
        if (rejections.length === clients.length) {
          reject(rejections);
        } else if (responses.length + rejections.length === clients.length) {
          resolve(
            responses.reduce((rateList, ratesResponse) => {
              if (Array.isArray(ratesResponse.data)) {
                ratesResponse.data.forEach(shippingRate => rateList.push(shippingRate));
              }
              return rateList;
            }, []),
          );
        }
      }

      /**
       * Register successful response resolution from rates request
       * @param {object} response
       */
      function resolution(response) {
        responses.push(response);
        rejectOrResolve();
      }

      /**
       * Register error response from rates request
       * @param {object} thrown
       */
      function rejection(thrown) {
        if (axios.isCancel(thrown)) {
          reject(thrown);
        } else {
          rejections.push(thrown);
          rejectOrResolve();
        }
      }

      clients.map(client =>
        makeRequest(
          'shipping-rates',
          {
            shippingClient: client,
            alias: itemAlias,
          },
          { address: destination },
          { cancelToken: this.requestCancelToken.token },
        )
          .then(resolution)
          .catch(rejection),
      );
    });
  }
}
